import { EMPTY_VALUE, fmtRatioPct, plural } from "../coachUtils";

// Coach Mode · Team model (Figma 02 · Team Roster, 02b · Team Performance
// and 02b · Modal · Team Archetype). Pure functions only, tested in
// src/tests/coachTeamModel.test.js.

export const TEAM_TABS = [
  { value: "roster", label: "Roster" },
  { value: "performance", label: "Performance" },
  { value: "nil", label: "NIL Intelligence" },
];

export const ROSTER_FILTERS = [
  { value: "all", label: "All players" },
  { value: "activated", label: "Activated" },
  { value: "pending", label: "Pending" },
  { value: "standouts", label: "Standouts" },
];

// "9 athletes on roster · 7 activated · 2 pending activation"
export const rosterSubtitle = (totals) =>
  `${plural(totals?.total ?? 0, "athlete")} on roster · ${totals?.activated ?? 0} activated · ${totals?.pending ?? 0} pending activation`;

// The six team style factors, in the modal's order (Figma copy verbatim).
export const TEAM_FACTORS = [
  { code: "EFF", title: "Offensive Efficiency", description: "How many points do you squeeze out of each possession?" },
  { code: "PACE", title: "Tempo", description: "How fast do you play? (possessions per game)" },
  { code: "SPACE", title: "3-point Reliance", description: "How much of your offense comes from three?" },
  { code: "BALL", title: "Ball Movement & Care", description: "Do you share the ball and protect it? (assist-turnover ratio)" },
  { code: "CRASH", title: "Offensive Rebounding", description: "How hard do you chase second-chance points?" },
  { code: "DISRUPT", title: "Defensive Activity", description: "How often do you force steals and blocks?" },
];

// Radar axes, clockwise from the top.
export const RADAR_ORDER = ["EFF", "DISRUPT", "CRASH", "BALL", "SPACE", "PACE"];

// Team archetype: the first rule whose conditions all hold names the team.
// Each condition is on a factor's league percentile: "high" is the 60th
// percentile or above (the top 40% of the league), "low" the 40th or below.
// The Figma example (PACE 70, SPACE 64) reads RUN-AND-GUN.
const HIGH = 60;
const LOW = 40;
export const TEAM_ARCHETYPES = [
  { label: "RUN-AND-GUN", when: { PACE: "high", SPACE: "high" }, description: "Plays fast and lets it fly from three." },
  { label: "HAVOC", when: { PACE: "high", DISRUPT: "high" }, description: "Speeds the game up with steals and blocks." },
  { label: "GRIT-AND-GRIND", when: { PACE: "low", CRASH: "high" }, description: "Slows it down and wins second chances on the glass." },
  { label: "MOTION OFFENSE", when: { BALL: "high", EFF: "high" }, description: "Shares the ball, protects it and scores efficiently." },
  { label: "SPACE-AND-SHOOT", when: { SPACE: "high" }, description: "Builds the offense around the three." },
  { label: "GLASS CRASHERS", when: { CRASH: "high" }, description: "Chases offensive rebounds and second-chance points." },
  { label: "LOCKDOWN", when: { DISRUPT: "high" }, description: "Defensive pressure that forces steals and blocks." },
  { label: "HALF-COURT", when: { PACE: "low" }, description: "Patient, slower possessions." },
  { label: "BALANCED", when: {}, description: "No single factor stands out against the league." },
];

const holds = (pct, level) => (level === "high" ? pct >= HIGH : pct <= LOW);

const percentiles = (factors) => Object.fromEntries((factors || []).map((f) => [f.code, f.pct]));

// True when every factor has a league percentile, so the radar can plot.
export const factorsRanked = (factors) => {
  const pcts = percentiles(factors);
  return TEAM_FACTORS.every((f) => pcts[f.code] != null);
};

export const teamArchetype = (factors) => {
  if (!factorsRanked(factors)) return null;
  const pcts = percentiles(factors);
  return TEAM_ARCHETYPES.find((a) => Object.entries(a.when).every(([code, level]) => holds(pcts[code], level)));
};

// "0.99 · 40": the raw value and the league percentile.
export const factorValueLabel = (factor) =>
  `${factor?.raw == null ? EMPTY_VALUE : Number(factor.raw).toFixed(2)} · ${factor?.pct ?? EMPTY_VALUE}`;

// Radar geometry (Figma: 140px outer ring, labels 30px outside it). Each
// vertex sits at the factor's percentile along its axis. `order` is the
// axis codes clockwise from the top, the team factors' unless given; the
// athlete profile's percentile radar passes its own stats.
export const radarGeometry = (factors, { cx, cy, radius, labelGap = 30, order = RADAR_ORDER }) => {
  const byCode = Object.fromEntries((factors || []).map((f) => [f.code, f]));
  return order.map((code, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / order.length;
    const at = (r) => ({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
    const factor = byCode[code] || { code };
    return { code, factor, axis: at(radius), vertex: at((radius * (factor.pct || 0)) / 100), label: at(radius + labelGap) };
  });
};

// Today's Team Performance Snapshot cells (Figma order), the snapshot's
// fallback when the game analytics are not there (TeamSnapshotCard). W-L,
// RPI and strength of schedule come from the game analytics alone: here
// they read as an en dash with a plain note, never a made-up number. The
// note is `gameNote`: by default that no game record is on file; while
// the analytics could not load or are still being prepared,
// GAME_RECORD_UNAVAILABLE (the record may well exist). The box-score cells
// rank the team in its league ("Top N%"). CacheValue per win is gone
// (BUILD_SPEC B7).
export const NO_GAME_RECORD = "No game record on file";
export const GAME_RECORD_UNAVAILABLE = "Not available right now";
const BOX_SCORE_STATS = [
  { key: "ppg", label: "PPG", format: (v) => Number(v).toFixed(1) },
  { key: "efg", label: "eFG", format: fmtRatioPct },
  { key: "threePct", label: "3P%", format: fmtRatioPct },
  { key: "ftr", label: "FTR", format: fmtRatioPct },
];

// Why the box-score cells are empty, by the style's `reason`.
export const styleNote = (style) => (style?.reason === "feed_error" ? "League feed unavailable right now" : "No box scores on file");

const noGameRecord = (key, label, note) => ({ key, label, value: EMPTY_VALUE, rank: null, note, boxScore: false });

export const snapshotCells = (style, { gameNote = NO_GAME_RECORD } = {}) => [
  noGameRecord("record", "W-L record", gameNote),
  ...BOX_SCORE_STATS.map(({ key, label, format }) => {
    const stat = style?.available ? style.snapshot?.[key] : null;
    const known = stat?.value != null;
    return {
      key,
      label,
      value: known ? format(stat.value) : EMPTY_VALUE,
      rank: known && stat.topPct ? `Top ${stat.topPct}%` : null,
      note: known ? null : styleNote(style),
      boxScore: true,
    };
  }),
  noGameRecord("rpi", "RPI rank", gameNote),
  noGameRecord("sos", "Strength of schedule", gameNote),
];
